import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, FileSearch } from 'lucide-react';
import SeverityBadge from './SeverityBadge';
import ProofStatusBadge from './ProofStatusBadge';
import ProvenanceDepthBar from './ProvenanceDepthBar';

/**
 * GovernanceTruthCard — canonical card for a single governance claim about an asset.
 * Replaces generic EvidenceCards on the Investigation Assets & Evidence tab.
 *
 * Each card asserts ONE claim, anchors it to a proof status + provenance depth,
 * names the controlling framework, and offers a pivot into the underlying record.
 *
 * Schema (`truth`):
 *   {
 *     id, asset, kind,                        // asset name + type
 *     severity,                               // 'critical' | 'high' | 'medium' | 'low'
 *     claim,                                  // governance claim sentence
 *     verdict: { tone, label },               // 'breach'|'risk'|'gap'|'clean' + short label
 *     proofStatus, reasonCode?, depth,        // ProofStatusBadge + ProvenanceDepthBar inputs
 *     sources: [str],                         // 'CloudTrail', 'Config', 'Macie' …
 *     frameworks: [str],                      // ['PCI DSS 3.4', 'SOC 2 CC6.3']
 *     evidenceId, lastVerified,               // provenance footer
 *     pivot?: { label, onClick }              // optional drill-into-record action
 *   }
 */

const VERDICT_TONE = {
  breach: 'bg-rose-50 text-rose-700 border-rose-200',
  risk:   'bg-amber-50 text-amber-700 border-amber-200',
  gap:    'bg-orange-50 text-orange-700 border-orange-200',
  clean:  'bg-emerald-50 text-emerald-700 border-emerald-200',
};

export default function GovernanceTruthCard({ truth, index = 0, testId }) {
  const {
    asset,
    kind,
    severity = 'medium',
    claim,
    verdict,
    proofStatus = 'PROVEN',
    reasonCode = null,
    depth = 7.5,
    sources = [],
    frameworks = [],
    evidenceId,
    lastVerified,
    pivot,
  } = truth;

  const verdictClass = VERDICT_TONE[verdict?.tone] ?? VERDICT_TONE.risk;

  const onActivate = (e) => {
    e?.stopPropagation?.();
    pivot?.onClick?.();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.06, ease: [0.16, 1, 0.3, 1] }}
      data-testid={testId ?? `truth-card-${asset}`}
      role={pivot ? 'button' : undefined}
      tabIndex={pivot ? 0 : undefined}
      onClick={pivot ? onActivate : undefined}
      onKeyDown={pivot ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onActivate(e); } } : undefined}
      className={`group bg-white rounded-lg border border-slate-200 p-4 transition-all hover:border-apex-lens/40 hover:shadow-sm ${pivot ? 'cursor-pointer' : ''}`}
    >
      {/* Header — asset + severity */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 mb-0.5">
            <FileSearch className="w-3 h-3 text-apex-neutral shrink-0" />
            <span className="text-[10px] uppercase tracking-wider font-mono text-apex-neutral">{kind}</span>
          </div>
          <h4 className="text-sm font-semibold text-apex-ink truncate" title={asset}>{asset}</h4>
        </div>
        <SeverityBadge severity={severity} size="sm" />
      </div>

      {/* Verdict tag */}
      {verdict?.label && (
        <span className={`inline-flex items-center text-[10px] font-mono font-semibold uppercase tracking-wider px-2 py-0.5 rounded border ${verdictClass} mb-2`}>
          {verdict.label}
        </span>
      )}

      {/* Governance claim */}
      <p className="text-[12.5px] text-apex-ink-2 leading-snug mb-3">
        {claim}
      </p>

      {/* Proof + depth row */}
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <ProofStatusBadge
          status={proofStatus}
          reasonCode={reasonCode}
          size="xs"
          testId={`${testId ?? `truth-card-${asset}`}-proof`}
          displayOnly
        />
        <ProvenanceDepthBar
          depth={depth}
          sources={sources}
          size="xs"
          testId={`${testId ?? `truth-card-${asset}`}-depth`}
        />
      </div>

      {/* Frameworks */}
      {frameworks.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {frameworks.map((f) => (
            <span
              key={f}
              className="text-[10px] font-mono text-apex-lens bg-teal-50 border border-teal-100 rounded px-1.5 py-0.5"
            >
              {f}
            </span>
          ))}
        </div>
      )}

      {/* Provenance footer */}
      <div className="pt-2 border-t border-slate-100 flex items-center justify-between gap-2 text-[10px] font-mono text-apex-neutral">
        <span className="truncate" title={evidenceId}>
          {evidenceId ?? '—'}
        </span>
        {lastVerified && (
          <span className="shrink-0">{lastVerified}</span>
        )}
      </div>

      {/* Pivot affordance */}
      {pivot && (
        <button
          type="button"
          onClick={onActivate}
          data-testid={`${testId ?? `truth-card-${asset}`}-pivot`}
          className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-apex-lens hover:text-apex-lens-dark transition-colors"
        >
          {pivot.label ?? 'Open record'}
          <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </button>
      )}
    </motion.div>
  );
}
